import { Button } from "@/components/common/Button/Button"
import { ButtonBase } from "@/components/common/ButtonBase/ButtonBase"
import { Progress } from "@/components/common/Progress/Progress"
import { Typography } from "@/components/common/Typography/Typography"
import { Button as GhostButton } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { classNames } from "@/utils/classNames"
import {
  IconCheck,
  IconChevronDown,
  IconExternalLink,
  IconFileSpreadsheet,
  IconFileUpload,
  IconInfoCircle,
} from "@tabler/icons-react"
import { AnimatePresence, useReducedMotion } from "motion/react"
import * as m from "motion/react-m"
import { useState } from "react"
import { type OnboardingBrowser, getExtensionStoreUrl } from "./extensionStatus"

const ARIBA_SUPPLIER_URL = "https://supplier.ariba.com"

export type StepId = "documents" | "extension" | "ariba"

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1]
const STAGGER = 0.08

/** Fade and rise in after `delay` seconds; a plain short fade when the user
 * prefers reduced motion. */
const useReveal = () => {
  const reduced = useReducedMotion()
  return (delay = 0) =>
    reduced
      ? {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.15 },
        }
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.4, delay, ease: EASE },
        }
}

interface ExternalButtonProps {
  href: string
  label: string
  variantColor: "yellow" | "dark"
  onClick?: () => void
}

const ExternalButton: React.FC<ExternalButtonProps> = ({
  href,
  label,
  variantColor,
  onClick,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className="inline-flex rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
  >
    <ButtonBase
      title={label}
      variantColor={variantColor}
      variantSize="medium"
      IconRight={IconExternalLink}
    />
  </a>
)

type StepState = "done" | "current" | "open"

const StepNode: React.FC<{ state: StepState; number: number }> = ({
  state,
  number,
}) => {
  const reduced = useReducedMotion()
  return (
    <div
      aria-hidden="true"
      className="absolute top-5 left-0 flex size-8 items-center justify-center"
    >
      {state === "current" && (
        <span className="absolute size-12 rounded-full bg-yellow1/25 blur-md motion-safe:animate-pulse" />
      )}
      {/* Keyed by state, so a step that gets done pops its check in. */}
      <m.span
        key={state}
        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduced ? 0.15 : 0.3, ease: EASE }}
        className={classNames(
          "relative flex size-8 items-center justify-center rounded-full",
          state === "done" &&
            "bg-yellow1/15 text-yellow1 ring-1 ring-yellow1/40",
          state === "current" && "bg-yellow1 text-black",
          state === "open" && "border border-grey4 bg-grey3 text-grey5",
        )}
      >
        {state === "done" ? (
          <IconCheck size={16} stroke={2.5} />
        ) : (
          <Typography
            variant="textS-bold"
            as="span"
            className={classNames(
              "tabular-nums",
              state === "current" ? "text-black" : "text-grey5",
            )}
          >
            {number}
          </Typography>
        )}
      </m.span>
    </div>
  )
}

interface StepCardProps {
  number: number
  title: string
  state: StepState
  isLast: boolean
  doneText: string
  doneLabel: string
  delay: number
  /** Explanation behind the title, collapsed so the card shows only the
   * step and its action. */
  details?: React.ReactNode
  children: React.ReactNode
}

const StepCard: React.FC<StepCardProps> = ({
  number,
  title,
  state,
  isLast,
  doneText,
  doneLabel,
  delay,
  details,
  children,
}) => {
  const reveal = useReveal()
  const reduced = useReducedMotion()
  const [detailsOpen, setDetailsOpen] = useState(false)
  return (
    <m.li {...reveal(delay)} className="relative pb-4 pl-12 last:pb-0">
      {!isLast && (
        <div
          aria-hidden="true"
          className="absolute top-14 -bottom-4 left-4 w-px -translate-x-1/2 bg-white/10"
        >
          {state === "done" && (
            <m.div
              className="absolute inset-0 origin-top bg-yellow1/50"
              initial={reduced ? false : { scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.4, delay: delay + 0.2, ease: EASE }}
            />
          )}
        </div>
      )}
      <StepNode state={state} number={number} />
      {state === "done" ? (
        <div className="flex items-center gap-4 rounded-2xl bg-grey2/50 px-6 py-5">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Typography variant="textM-bold" as="h3" className="text-grey5">
              {title}
            </Typography>
            <Typography variant="textS" className="text-grey5">
              {doneText}
            </Typography>
          </div>
          <Typography
            variant="textXS-bold"
            as="span"
            className="shrink-0 rounded-full bg-yellow1/10 px-3 py-1 text-yellow1"
          >
            {doneLabel}
          </Typography>
        </div>
      ) : (
        <div
          className={classNames(
            "flex flex-col gap-4 rounded-2xl border p-6",
            state === "current"
              ? "border-yellow1/40 bg-grey2"
              : "border-transparent bg-grey2/60",
          )}
        >
          {details ? (
            <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
              <Typography variant="textM-bold" as="h3">
                <CollapsibleTrigger className="group flex w-full items-center justify-between gap-4 rounded-md text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden">
                  {title}
                  <IconChevronDown
                    size={18}
                    aria-hidden="true"
                    className="shrink-0 text-grey5 transition-transform duration-200 group-hover:text-white group-data-[state=open]:rotate-180"
                  />
                </CollapsibleTrigger>
              </Typography>
              <AnimatePresence initial={false}>
                {detailsOpen && (
                  <CollapsibleContent forceMount asChild>
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: reduced ? 0.15 : 0.25,
                        ease: EASE,
                      }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-3 pt-3">{details}</div>
                    </m.div>
                  </CollapsibleContent>
                )}
              </AnimatePresence>
            </Collapsible>
          ) : (
            <Typography variant="textM-bold" as="h3">
              {title}
            </Typography>
          )}
          {children}
        </div>
      )}
    </m.li>
  )
}

const StepText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="textS"
    className="leading-relaxed! text-pretty text-grey5"
  >
    {children}
  </Typography>
)

/** Live status of step 2 while the page waits for the extension. */
const ExtensionStatusChip: React.FC<{
  installed: boolean
  label: string
}> = ({ installed, label }) => (
  <div
    aria-live="polite"
    className="flex w-fit items-center gap-2 rounded-lg border border-white/10 bg-grey3/60 px-3 py-2"
  >
    <span aria-hidden="true" className="relative flex size-2 shrink-0">
      <span
        className={classNames(
          "absolute inline-flex size-full rounded-full opacity-60 motion-safe:animate-ping",
          installed ? "bg-warning" : "bg-yellow1",
        )}
      />
      <span
        className={classNames(
          "relative inline-flex size-2 rounded-full",
          installed ? "bg-warning" : "bg-yellow1",
        )}
      />
    </span>
    <Typography variant="textXS" className="text-grey5">
      {label}
    </Typography>
  </div>
)

export interface AribaOnboardingViewProps {
  browser: OnboardingBrowser
  uploadedFiles: number
  extensionInstalled: boolean
  documentsDone: boolean
  extensionDone: boolean
  aribaDone: boolean
  /** First open step; undefined once all three are done. */
  currentStep: StepId | undefined
  onStoreClick: () => void
  onExtensionInstalled: () => void
  onDismiss: () => void
  onClose: () => void
}

export const AribaOnboardingView: React.FC<AribaOnboardingViewProps> = ({
  browser,
  uploadedFiles,
  extensionInstalled,
  documentsDone,
  extensionDone,
  aribaDone,
  currentStep,
  onStoreClick,
  onExtensionInstalled,
  onDismiss,
  onClose,
}) => {
  const { translate } = useTranslation()
  const reveal = useReveal()
  const reduced = useReducedMotion()

  if (currentStep === undefined) {
    return (
      <section className="mx-auto flex min-h-full w-full max-w-[640px] flex-col items-center justify-center px-6 py-16 text-center">
        <div className="relative mb-10 flex items-center justify-center">
          <m.span
            aria-hidden="true"
            className="absolute size-28 rounded-full bg-yellow1/25 blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: EASE }}
          />
          <m.span
            aria-hidden="true"
            className="relative flex size-16 items-center justify-center rounded-full bg-yellow1 text-black"
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <IconCheck size={32} stroke={2.5} />
          </m.span>
        </div>
        <m.div {...reveal(0.15)} className="flex flex-col items-center gap-4">
          <Typography
            variant="titleL"
            as="h2"
            className="tracking-[-0.02em] text-balance"
            style={{ fontSize: "clamp(1.5rem, 2.5vw, 1.875rem)" }}
          >
            {translate("ARIBA_ONBOARDING/FINISHED_TITLE")}
          </Typography>
          <Typography
            variant="textM"
            className="leading-relaxed! text-pretty text-grey5"
          >
            {translate("ARIBA_ONBOARDING/FINISHED_TEXT")}
          </Typography>
        </m.div>
        <m.div
          {...reveal(0.25)}
          className="mt-10 flex flex-wrap justify-center gap-S"
        >
          <Button
            path="/questionnaire/upload"
            variantColor="yellow"
            variantSize="medium"
            label={translate("ARIBA_ONBOARDING/FINISHED_UPLOAD")}
            title={translate("ARIBA_ONBOARDING/FINISHED_UPLOAD")}
            IconLeft={IconFileUpload}
          />
          <Button
            variantColor="dark"
            variantSize="medium"
            label={translate("ARIBA_ONBOARDING/FINISHED_HOME")}
            title={translate("ARIBA_ONBOARDING/FINISHED_HOME")}
            onClick={onClose}
          />
        </m.div>
      </section>
    )
  }

  const doneCount = [documentsDone, extensionDone, aribaDone].filter(
    Boolean,
  ).length
  const stateOf = (id: StepId, isDone: boolean): StepState =>
    isDone ? "done" : currentStep === id ? "current" : "open"
  const colorOf = (id: StepId) => (currentStep === id ? "yellow" : "dark")
  const storeUrl = getExtensionStoreUrl(browser)
  const doneLabel = translate("ARIBA_ONBOARDING/DONE")
  const uploadedText =
    uploadedFiles === 1
      ? translate("ARIBA_ONBOARDING/DOCUMENTS_DONE_ONE")
      : translate("ARIBA_ONBOARDING/DOCUMENTS_DONE_MANY", {
          count: uploadedFiles,
        })

  return (
    <section className="mx-auto grid w-full max-w-[1200px] items-start gap-10 px-6 py-12 sm:px-10 sm:py-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
      <m.header
        {...reveal(0)}
        className="flex flex-col gap-8 lg:sticky lg:top-16"
      >
        <div className="flex flex-col gap-3">
          <Typography
            variant="titleL"
            as="h2"
            className="tracking-[-0.02em] text-balance"
            style={{ fontSize: "clamp(1.75rem, 2.4vw, 2.25rem)" }}
          >
            {translate("ARIBA_ONBOARDING/TITLE")}
          </Typography>
          <Typography
            variant="textM"
            className="leading-relaxed! text-pretty text-grey5"
          >
            {translate("ARIBA_ONBOARDING/SUBTITLE")}
          </Typography>
        </div>
        <div className="flex items-center gap-4">
          {/* common/Progress uses value only to draw the bar, so the count is
              stated for screen readers here. */}
          <Progress
            value={(doneCount / 3) * 100}
            aria-label={doneLabel}
            aria-valuenow={doneCount}
            aria-valuemax={3}
            aria-valuetext={`${doneCount}/3`}
            className="h-1 flex-1 bg-white/10"
            indicatorClassName="duration-[400ms] ease-out"
          />
          <Typography
            variant="textS-bold"
            as="span"
            className="shrink-0 text-grey5 tabular-nums"
          >
            <span className="text-white">{doneCount}</span>/3
          </Typography>
        </div>
        <div>
          <GhostButton
            variant="ghost"
            size="sm"
            className="-ml-3 text-grey5 hover:text-white"
            onClick={onDismiss}
          >
            {translate("ARIBA_ONBOARDING/LATER")}
          </GhostButton>
        </div>
      </m.header>

      <div className="flex min-w-0 flex-col">
        <ol className="flex flex-col">
          <StepCard
            number={1}
            title={translate("ARIBA_ONBOARDING/DOCUMENTS_TITLE")}
            state={stateOf("documents", documentsDone)}
            isLast={false}
            doneText={uploadedText}
            doneLabel={doneLabel}
            delay={STAGGER}
            details={
              <StepText>
                {translate("ARIBA_ONBOARDING/DOCUMENTS_TEXT")}
              </StepText>
            }
          >
            <div>
              <Button
                path="/knowledgebase"
                variantColor={colorOf("documents")}
                variantSize="medium"
                label={translate("ARIBA_ONBOARDING/DOCUMENTS_BUTTON")}
                title={translate("ARIBA_ONBOARDING/DOCUMENTS_BUTTON")}
                IconLeft={IconFileUpload}
              />
            </div>
          </StepCard>

          <StepCard
            number={2}
            title={translate("ARIBA_ONBOARDING/EXTENSION_TITLE")}
            state={stateOf("extension", extensionDone)}
            isLast={false}
            doneText={translate("ARIBA_ONBOARDING/EXTENSION_DONE")}
            doneLabel={doneLabel}
            delay={STAGGER * 2}
            details={
              storeUrl === null ? undefined : (
                <StepText>
                  {translate("ARIBA_ONBOARDING/EXTENSION_TEXT")}
                </StepText>
              )
            }
          >
            {storeUrl === null ? (
              <StepText>
                {translate("ARIBA_ONBOARDING/EXTENSION_OTHER_BROWSER")}
              </StepText>
            ) : (
              <>
                {browser === "edge" && (
                  <StepText>
                    {translate("ARIBA_ONBOARDING/EXTENSION_EDGE_HINT")}
                  </StepText>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <ExternalButton
                    href={storeUrl}
                    variantColor={colorOf("extension")}
                    label={translate(
                      browser === "firefox"
                        ? "ARIBA_ONBOARDING/EXTENSION_BUTTON_FIREFOX"
                        : "ARIBA_ONBOARDING/EXTENSION_BUTTON_CHROME",
                    )}
                    onClick={onStoreClick}
                  />
                  {(browser === "firefox" || !extensionInstalled) && (
                    <Button
                      variantColor="dark"
                      variantSize="medium"
                      label={translate(
                        "ARIBA_ONBOARDING/EXTENSION_INSTALLED_CONFIRM",
                      )}
                      title={translate(
                        "ARIBA_ONBOARDING/EXTENSION_INSTALLED_CONFIRM",
                      )}
                      onClick={onExtensionInstalled}
                    />
                  )}
                </div>
                {browser !== "firefox" && (
                  <ExtensionStatusChip
                    installed={extensionInstalled}
                    label={translate(
                      extensionInstalled
                        ? "ARIBA_ONBOARDING/EXTENSION_SIGN_IN"
                        : "ARIBA_ONBOARDING/EXTENSION_WAITING",
                    )}
                  />
                )}
              </>
            )}
          </StepCard>

          <StepCard
            number={3}
            title={translate("ARIBA_ONBOARDING/ARIBA_TITLE")}
            state={stateOf("ariba", aribaDone)}
            isLast
            doneText={translate("ARIBA_ONBOARDING/ARIBA_DONE")}
            doneLabel={doneLabel}
            delay={STAGGER * 3}
            details={
              <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-grey5">
                {(
                  ["ARIBA_STEP_1", "ARIBA_STEP_2", "ARIBA_STEP_3"] as const
                ).map((key) => (
                  <li key={key} className="pl-1">
                    <StepText>{translate(`ARIBA_ONBOARDING/${key}`)}</StepText>
                  </li>
                ))}
              </ol>
            }
          >
            <div>
              <ExternalButton
                href={ARIBA_SUPPLIER_URL}
                variantColor={colorOf("ariba")}
                label={translate("ARIBA_ONBOARDING/ARIBA_BUTTON")}
              />
            </div>
          </StepCard>
        </ol>

        <m.div
          {...reveal(STAGGER * 4)}
          className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 pl-12"
        >
          {/* A popover, not a tooltip: Radix tooltips never open on touch. */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md text-left text-grey5 hover:text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
              >
                <Typography
                  variant="textS-bold"
                  as="span"
                  className="text-inherit"
                >
                  {translate("ARIBA_ONBOARDING/FALLBACK_TITLE")}
                </Typography>
                <IconInfoCircle size={16} aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-80">
              <Typography variant="textS" className="leading-relaxed!">
                {translate("ARIBA_ONBOARDING/FALLBACK_TEXT")}
              </Typography>
            </PopoverContent>
          </Popover>
          <Button
            path="/questionnaire/upload"
            variantColor="dark"
            variantSize="small"
            label={translate("ARIBA_ONBOARDING/FALLBACK_BUTTON")}
            title={translate("ARIBA_ONBOARDING/FALLBACK_BUTTON")}
            IconLeft={IconFileSpreadsheet}
            className="shrink-0"
          />
        </m.div>
      </div>
    </section>
  )
}
